import { Badge } from "@/components/Badge.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { vmQueryAtom } from "@/modules/compute/vmQuery.ts";
import { AssignedToSelect, type AssignedToSelectParams } from "@/modules/network/firewall/AssignedToSelect.tsx";
import { convertTargetType } from "@/modules/network/firewall/firewall.logic.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import type { FirewallAssignedResource } from "@warrenio/api-spec/spec.oats.gen";
import { splice } from "remeda";

export interface AssignedToListProps extends Omit<AssignedToSelectParams, "usedResourceUuids"> {
    location: string;
    onInputChange: (value: FirewallAssignedResource[]) => void;
    value: FirewallAssignedResource[];
}

function ItemBadge({ item }: { item?: VirtualMachineLoc }) {
    return <Badge color={item?.status === "running" ? "success" : "error"}>{item?.status}</Badge>;
}

export function AssignedToList({ value, onInputChange, ...props }: AssignedToListProps) {
    const { location } = props;
    const vms = useSuspenseQueryAtom(vmQueryAtom(location));

    function onAdd(vm: VirtualMachineLoc) {
        onInputChange([...value, { resource_type: "vm" as const, resource_uuid: vm.uuid }]);
    }

    function onDelete(index: number) {
        onInputChange(splice(value, index, 1, []));
    }

    return (
        <>
            <div className="mb-3 lightBlock">
                <AssignedToSelect
                    // Always keep nothing selected
                    valueKey={null}
                    onChange={onAdd}
                    usedResourceUuids={value.map((a) => a.resource_uuid)}
                    {...props}
                />
            </div>

            <WTable>
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>Private IP</Trans>
                        </th>
                        <th>
                            <Trans>Status</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {value.map((item, index) => {
                        const resource = vms.get(item.resource_uuid);

                        return (
                            <tr key={index}>
                                <td>{resource?.name}</td>
                                <td>
                                    <ResourceWithIcon type={convertTargetType(item.resource_type)} />
                                </td>
                                <td>{resource?.private_ipv4}</td>
                                <td>
                                    <ItemBadge item={resource} />
                                </td>
                                <td className="text-right">
                                    <DeleteButton inTable action={() => onDelete(index)} />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
            </WTable>
        </>
    );
}
